import { cloneDeep, uniq } from 'lodash-es'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { hmToMinutes, minutesToHm, normalizeTime } from '@/common/composables/useDatetime'
import { TableWithReservationAndUpdates } from '@/common/interfaces/TableBulkEditing'
import { useCSAreaStore } from '@/modules/continuousService/stores/CSAreaStore'
import CSDateService from '@/modules/csDate/services/CSDateService'
import CSReservationService from '@/modules/csDate/services/CSReservationService'
import { TABLE_UPDATE_STATUS } from '@/modules/performance/interfaces/PerformanceTableInterfaces'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { useSettingStore } from '@/stores/setting'
import { upsertById } from '@/utils/utils'

import {
  CSAreaId,
  CSDateId,
  CSDateRead,
  CSDateReservationsRead,
  CSDateTableRead,
  CSDateWrite,
  CSInventoryBlockRead,
  CSReservationCreateWrite,
  CSReservationRead,
  CSReservationWrite,
  PerformanceQueryParams,
  ReservationStatus,
  ReservationStatusType,
} from '@generated/types'

interface CSTimeSlot {
  start_time: string
  available_guests: number
  reservations: CSReservationRead[]
  is_available: boolean
}

interface FetchParams {
  csAreaId: CSAreaId
  date: string
}

interface Availability {
  start_time: string
  available_guests: number
}

export type CSDateTableWithReservations = TableWithReservationAndUpdates<CSDateTableRead>

export const useCSDateStore = defineStore('csDate', function () {
  const csDatesPerSelectedAreaId = ref<CSDateRead[]>([])
  const selectedCSDate = ref<CSDateReservationsRead | null>(null)
  const csReservations = computed<CSReservationRead[]>(() => {
    if (!selectedCSDate.value) return []
    return selectedCSDate.value.reservations || []
  })

  const csAreaStore = useCSAreaStore()
  const { csAreas } = storeToRefs(csAreaStore)
  const initialized = ref<boolean>(true)
  async function initialize(params: FetchParams) {
    initialized.value = false
    const csAreaId = params.csAreaId || csAreas.value[0]?.id
    const csAreaExists = csAreas.value.some(csArea => csArea.id === csAreaId)
    if (!csAreaExists) {
      csAreas.value = [] // It will display #no-areas div
    } else {
      await Promise.all([
        fetchAllCSDatesPerMonth({ date: params.date, csAreaId }),
        fetchSelectedCSDateInfo({ date: params.date, csAreaId }),
      ])
    }
    initialized.value = true
  }

  const settingStore = useSettingStore()
  async function fetchAllCSDatesPerMonth(params: FetchParams) {
    const startOfMonth = settingStore.parseMoment(params.date).startOf('month').format('YYYY-MM-DD')
    const endOfMonth = settingStore.parseMoment(params.date).endOf('month').format('YYYY-MM-DD')
    const { data: csDates } = await CSDateService.listCSDatesForArea({
      csarea_id: params.csAreaId,
      start_date: startOfMonth,
      end_date: endOfMonth,
    })
    for (const date of csDates) upsertById(date, csDatesPerSelectedAreaId.value)
  }

  async function fetchSelectedCSDateInfo(params: FetchParams) {
    try {
      const { data } = await CSDateService.getCSDateReservations(params.csAreaId, params.date)
      selectedCSDate.value = cloneDeep(data)
    } catch (e) {
      selectedCSDate.value = null
    }
  }

  const timeSlots = computed<CSTimeSlot[]>(() => {
    if (!selectedCSDate.value) return []

    const availabilityArr: Array<Availability> = selectedCSDate.value.availability || []
    const availabilityMap = new Map<string, number>()
    for (const a of availabilityArr) {
      availabilityMap.set(normalizeTime(a.start_time), Number(a.available_guests) || 0)
    }

    const blocks: CSInventoryBlockRead[] = selectedCSDate.value.inventory || []
    const step = selectedCSDate.value.csarea?.period_minutes ?? 10
    const derivedFromConfig: string[] = []
    for (const b of blocks) {
      const startM = hmToMinutes(b.start)
      const endM = hmToMinutes(b.end)
      for (let t = startM; t <= endM; t += step) {
        derivedFromConfig.push(normalizeTime(minutesToHm(t)))
      }
    }

    const fromAvailability = availabilityArr.map(a => normalizeTime(a.start_time))
    const fromReservations = csReservations.value.map(r =>
      normalizeTime((r as any).start_time as string),
    )

    const combined = uniq([...derivedFromConfig, ...fromAvailability, ...fromReservations])
    combined.sort((a, b) => hmToMinutes(a) - hmToMinutes(b))

    const reservationsByTime: Record<string, CSReservationRead[]> = {}
    for (const r of csReservations.value) {
      const key = normalizeTime((r as any).start_time as string)
      if (!reservationsByTime[key]) reservationsByTime[key] = []
      reservationsByTime[key].push(r as CSReservationRead)
    }

    return combined.map(time => ({
      start_time: time,
      available_guests: availabilityMap.get(time) ?? 0,
      is_available: (availabilityMap.get(time) ?? 0) > 0,
      reservations: reservationsByTime[time] ?? [],
    }))
  })
  const availableTimeSlots = computed<CSTimeSlot[]>(() =>
    timeSlots.value.filter(t => t.is_available),
  )

  const tablesWithReservations = computed<CSDateTableWithReservations[]>(() => {
    const tables = selectedCSDate.value?.tables || []
    if (!selectedCSDate.value || !tables) return []

    return tables.map((table: CSDateTableRead) => {
      const activeReservation = csReservations.value.find(reservation => {
        const tableIds = reservation.tables || []
        return tableIds?.includes(table.id)
      })

      return {
        ...table,
        activeReservation: activeReservation || null,
        inactiveReservations: [],
        reservationStatus: activeReservation
          ? activeReservation.status
          : TABLE_UPDATE_STATUS.AVAILABLE,
        updateStatus: TABLE_UPDATE_STATUS.AVAILABLE,
        hidden: false,
        allowToDelete: false,
        hasUpdates: false,
      }
    })
  })

  const csDates = ref<CSDateRead[]>([])
  async function getCSDates(query: PerformanceQueryParams) {
    const response = await CSDateService.getCSDates(query)
    return response.data.map(date => {
      upsertById(date, csDates.value)
    })
  }
  async function getCSDateReservations(csAreaId: CSAreaId, date: string) {
    const response = await CSDateService.getCSDateReservations(csAreaId, date)
    if (response.data.reservations) {
      const reservationStore = useReservationStore()
      response.data.reservations.map(reservationStore.storeReservation)
    }
    return response.data
  }
  async function getCSDateReservationsById(csDateId: number) {
    const response = await CSDateService.getCSDateReservationsById(csDateId)
    if (response.data.reservations) {
      const reservationStore = useReservationStore()
      response.data.reservations.map(reservationStore.storeReservation)
    }
    return response.data
  }
  async function getCSDate(csDateId: CSDateId) {
    const response = await CSDateService.getCSDate(csDateId)
    return response.data
  }

  async function createCSDate(csDate: CSDateWrite) {
    const { data: newCsDate } = await CSDateService.createCSDate(csDate)
    upsertById(newCsDate, csDatesPerSelectedAreaId.value)
    upsertById(newCsDate, csDates.value)
    await fetchSelectedCSDateInfo({ csAreaId: newCsDate.csarea_id, date: newCsDate.date })
    return newCsDate
  }

  async function updateCSReservation(reservation: CSReservationWrite) {
    const { data } = await CSReservationService.updateCSReservation(reservation.id!, reservation)
    // Refresh reservations and availability
    if (selectedCSDate.value) {
      await fetchSelectedCSDateInfo({
        date: selectedCSDate.value.date,
        csAreaId: selectedCSDate.value.csarea_id,
      })
    }
    return data
  }

  async function seatWalkIn(
    csDate: CSDateReservationsRead,
    tableIds: number[],
    numberOfGuests?: number,
  ) {
    const startTime = csDate.availability.find(a => a.available_guests > 0)?.start_time
    return await createCSReservation({
      status: ReservationStatus.SEATED,
      csdate_id: csDate.id,
      duration: csDate.duration || 0,
      number_of_guests: numberOfGuests || 0,
      start_time: startTime,
      tables: tableIds,
      skip_guest: true,
    })
  }

  async function setReservationStatus(
    reservation: CSReservationRead,
    status: ReservationStatusType,
  ) {
    const reservationResponse = await updateCSReservation({
      id: reservation.id,
      status,
      start_time: reservation.start_time,
      duration: reservation.duration,
      number_of_guests: reservation.number_of_guests,
      guest_notes: reservation.guest_notes,
      internal_notes: reservation.internal_notes,
    })
    return { reservation: reservationResponse }
  }

  async function createCSReservation(reservation: CSReservationCreateWrite) {
    const { data } = await CSReservationService.createCSReservation(reservation)
    // Refresh reservations and availability
    if (selectedCSDate.value) {
      await fetchSelectedCSDateInfo({
        date: selectedCSDate.value.date,
        csAreaId: selectedCSDate.value.csarea_id,
      })
    }
    return data
  }

  async function getCSReservation(reservationId: number) {
    const { data: reservation } = await CSReservationService.getCSReservation(reservationId)
    if (selectedCSDate.value) upsertById(reservation, selectedCSDate.value.reservations)
    return reservation
  }

  return {
    fetchAllCSDatesPerMonth,
    csDatesPerSelectedAreaId,
    createCSDate,
    seatWalkIn,
    selectedCSDate,
    csReservations,
    timeSlots,
    availableTimeSlots,
    tablesWithReservations,
    fetchSelectedCSDateInfo,
    initialized,
    initialize,
    updateCSReservation,
    setReservationStatus,
    createCSReservation,
    getCSReservation,

    csDates,
    getCSDates,
    getCSDate,
    getCSDateReservations,
    getCSDateReservationsById,
  }
})
